import React from 'react';
import { uploadImage, uploadFile, deleteImageFromStorage, deleteFileFromStorage } from '../../firebase/firebase-config';

function GlobalBannerSettings({ settings, setSettings, uploading, uploadingField, setUploading, setUploadingField }) {
  const handleImageUpload = async (idx, file) => {
    if (!file) return;
    if (setUploading) setUploading(true);
    if (setUploadingField) setUploadingField(`globalBanner_${idx}`);

    try {
      const url = await uploadImage(file, `global/banner_${Date.now()}`);
      const newImages = [...(settings.globalBannerImages || [])];
      if (newImages[idx]) {
        newImages[idx] = { ...newImages[idx], url };
      } else {
        newImages.push({ url });
      }
      setSettings({ ...settings, globalBannerImages: newImages });
      alert('✅ Image uploaded successfully!');
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('❌ Error uploading image: ' + error.message);
    } finally {
      if (setUploading) setUploading(false);
      if (setUploadingField) setUploadingField('');
    }
  };

  const handleVideoUpload = async (file) => {
    if (!file) return;
    if (setUploading) setUploading(true);
    if (setUploadingField) setUploadingField('globalBannerVideo');

    try {
      const videoData = await uploadFile(file, `global/banner_${Date.now()}`);
      setSettings({ ...settings, globalBannerVideo: videoData.url, useVideoBanner: true });
      alert('✅ Video uploaded successfully!');
    } catch (error) {
      console.error('Error uploading video:', error);
      alert('❌ Error uploading video: ' + error.message);
    } finally {
      if (setUploading) setUploading(false);
      if (setUploadingField) setUploadingField('');
    }
  };

  const deleteImage = async (idx) => {
    const img = settings.globalBannerImages[idx];
    if (!img?.url) {
      setSettings({ ...settings, globalBannerImages: settings.globalBannerImages.filter((_, i) => i !== idx) });
      return;
    }

    if (window.confirm('Delete this image permanently?')) {
      if (setUploading) setUploading(true);

      try {
        await deleteImageFromStorage(img.url);
        setSettings({ ...settings, globalBannerImages: settings.globalBannerImages.filter((_, i) => i !== idx) });
        alert('✅ Image deleted successfully!');
      } catch (error) {
        console.error('Error deleting image:', error);
        alert('❌ Error deleting image: ' + error.message);
      } finally {
        if (setUploading) setUploading(false);
      }
    }
  };

  const deleteVideo = async () => {
    if (!settings.globalBannerVideo) {
      setSettings({ ...settings, globalBannerVideo: '', useVideoBanner: false });
      return;
    }

    if (window.confirm('Delete this video permanently?')) {
      if (setUploading) setUploading(true);

      try {
        await deleteFileFromStorage(settings.globalBannerVideo);
        setSettings({ ...settings, globalBannerVideo: '', useVideoBanner: false });
        alert('✅ Video deleted successfully!');
      } catch (error) {
        console.error('Error deleting video:', error);
        alert('❌ Error deleting video: ' + error.message);
      } finally {
        if (setUploading) setUploading(false);
      }
    }
  };

  // CSS for the hero slider to prevent cropping and ensure full image visibility
  const sliderStyles = `
    .global-hero-slider {
      position: relative;
      width: 100%;
      overflow: hidden;
      background: #0a0a0a;
    }

    /* Desktop: 887x500 ratio container */
    .global-hero-slider .slick-slider,
    .global-hero-slider .slick-list,
    .global-hero-slider .slick-track {
      height: 100%;
    }

    .global-hero-slider .slick-slide {
      position: relative;
      height: auto;
    }

    /* Image container with proper aspect ratio */
    .banner-image-container {
      position: relative;
      width: 100%;
      background: #0a0a0a;
      overflow: hidden;
    }

    /* Desktop height */
    @media (min-width: 1024px) {
      .banner-image-container {
        height: 500px;
        max-height: 70vh;
      }
    }

    /* Tablet */
    @media (min-width: 768px) and (max-width: 1023px) {
      .banner-image-container {
        height: 450px;
        max-height: 60vh;
      }
    }

    /* Mobile: 375x500 ratio with full image visible */
    @media (max-width: 767px) {
      .banner-image-container {
        height: auto;
        min-height: 500px;
        aspect-ratio: 375 / 500;
      }
    }

    /* Image styling - NO CROP, full image visible */
    .hero-banner-image {
      width: 100%;
      height: 100%;
      object-fit: contain;
      object-position: center;
      background: #0a0a0a;
    }

    /* Blurred background layer for contain mode */
    .banner-background-blur {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-size: cover;
      background-position: center;
      filter: blur(20px);
      transform: scale(1.1);
      opacity: 0.6;
      z-index: 1;
    }

    .banner-foreground {
      position: relative;
      z-index: 2;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    /* Overlay for text visibility */
    .banner-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(90deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.2) 100%);
      z-index: 2;
      pointer-events: none;
    }

    /* Video styling */
    .hero-banner-video {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }

    /* Mobile video handling */
    @media (max-width: 767px) {
      .hero-banner-video {
        object-fit: contain;
        background: #0a0a0a;
      }
    }

    /* Custom arrow styling */
    .slick-arrow {
      z-index: 10;
      width: 44px;
      height: 44px;
      border-radius: 50%;
      background: rgba(0,0,0,0.5);
      backdrop-filter: blur(4px);
      transition: all 0.3s ease;
    }

    .slick-arrow:hover {
      background: rgba(0,0,0,0.8);
      transform: scale(1.05);
    }

    .slick-prev {
      left: 20px;
    }

    .slick-next {
      right: 20px;
    }

    .slick-prev:before,
    .slick-next:before {
      font-size: 24px;
      opacity: 1;
    }

    /* Dots styling */
    .slick-dots {
      bottom: 20px;
      z-index: 10;
    }

    .slick-dots li button:before {
      font-size: 12px;
      color: white;
      opacity: 0.6;
    }

    .slick-dots li.slick-active button:before {
      color: #ffffff;
      opacity: 1;
    }

    /* Touch friendly for mobile */
    @media (max-width: 767px) {
      .slick-prev {
        left: 10px;
      }
      .slick-next {
        right: 10px;
      }
      .slick-prev:before,
      .slick-next:before {
        font-size: 18px;
      }
      .slick-dots {
        bottom: 10px;
      }
      .slick-dots li button:before {
        font-size: 10px;
      }
    }

    /* Loading state */
    .banner-loading {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: white;
      z-index: 5;
    }

    /* Admin panel styles (kept original) */
    .global-banner-editor .slider-item {
      transition: all 0.2s ease;
    }
    .global-banner-editor .slider-item:hover {
      background: #f1f5f9;
    }
    .preview-img-sm {
      width: 80px;
      height: 60px;
      object-fit: cover;
      border-radius: 6px;
      background: #e2e8f0;
    }
    .upload-label {
      background: #3b82f6;
      color: white;
      padding: 8px 16px;
      border-radius: 8px;
      font-size: 14px;
      transition: background 0.2s;
    }
    .upload-label:hover {
      background: #2563eb;
    }
    .remove-btn {
      background: #ef4444;
      color: white;
      border: none;
      padding: 8px 16px;
      border-radius: 8px;
      cursor: pointer;
      font-size: 14px;
      transition: background 0.2s;
    }
    .remove-btn:hover {
      background: #dc2626;
    }
    .add-btn {
      background: #10b981;
      color: white;
      border: none;
      padding: 10px 20px;
      border-radius: 8px;
      cursor: pointer;
      font-size: 14px;
      transition: background 0.2s;
    }
    .add-btn:hover {
      background: #059669;
    }
    .editor-card {
      background: white;
      border-radius: 16px;
      padding: 24px;
      margin-bottom: 24px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    }
    .form-group {
      margin-bottom: 20px;
    }
  `;

  return (
    <>
      <style>{sliderStyles}</style>
      <div className="editor-card">
        <h3>🌐 Global Header Banner (Shows on ALL Pages)</h3>
        <div className="form-group">
          <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="radio"
                name="bannerType"
                checked={!settings.useVideoBanner}
                onChange={() => setSettings({ ...settings, useVideoBanner: false })}
              />
              Image Slider
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="radio"
                name="bannerType"
                checked={settings.useVideoBanner}
                onChange={() => setSettings({ ...settings, useVideoBanner: true })}
              />
              Video Banner
            </label>
          </div>
        </div>

        {!settings.useVideoBanner ? (
          <div className="form-group">
            <label>Banner Images</label>
            <div className="global-banner-editor" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {(settings.globalBannerImages || []).map((img, idx) => (
                <div key={idx} className="slider-item" style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
                  {img.url && (
                    <img
                      src={img.url}
                      alt={`Banner ${idx + 1}`}
                      className="preview-img-sm"
                      style={{ width: '80px', height: '60px', objectFit: 'cover', borderRadius: '6px' }}
                    />
                  )}
                  <label className="upload-label" style={{ cursor: 'pointer', margin: 0 }}>
                    {uploading && uploadingField === `globalBanner_${idx}` ? '⏳ Uploading...' : '📷 Choose Image'}
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={async (e) => {
                        if (e.target.files[0]) {
                          await handleImageUpload(idx, e.target.files[0]);
                        }
                      }}
                    />
                  </label>
                  <button
                    className="remove-btn"
                    onClick={() => deleteImage(idx)}
                    style={{ marginLeft: 'auto' }}
                  >
                    🗑️ Delete
                  </button>
                </div>
              ))}
              <button
                className="add-btn"
                onClick={() => setSettings({ ...settings, globalBannerImages: [...(settings.globalBannerImages || []), { url: '' }] })}
                style={{ alignSelf: 'flex-start' }}
              >
                + Add Image
              </button>
            </div>
            <small style={{ display: 'block', marginTop: '8px', color: '#64748b' }}>
              Recommended image size: 1920x600 pixels. Images will be displayed in a slider without cropping.
            </small>
          </div>
        ) : (
          <div className="form-group">
            <label>Banner Video</label>
            <div className="video-upload-area" style={{ border: '2px dashed #cbd5e1', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
              {settings.globalBannerVideo && (
                <video
                  src={settings.globalBannerVideo}
                  controls
                  style={{ maxWidth: '100%', maxHeight: '200px', marginBottom: '16px' }}
                />
              )}
              <label className="upload-label" style={{ cursor: 'pointer', display: 'inline-block' }}>
                {uploading && uploadingField === 'globalBannerVideo' ? '⏳ Uploading...' : '📹 Upload MP4 Video'}
                <input
                  type="file"
                  accept="video/mp4"
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    if (e.target.files[0]) {
                      await handleVideoUpload(e.target.files[0]);
                    }
                  }}
                />
              </label>
              {settings.globalBannerVideo && (
                <button
                  className="remove-btn"
                  onClick={deleteVideo}
                  style={{ marginLeft: '12px' }}
                >
                  🗑️ Delete Video
                </button>
              )}
            </div>
            <small style={{ display: 'block', marginTop: '8px', color: '#64748b' }}>
              Recommended video size: 1920x600 pixels, MP4 format, max 10MB.
            </small>
          </div>
        )}
      </div>
    </>
  );
}

export default GlobalBannerSettings;